import React, { useState, useEffect } from 'react';
import { GlassButton } from '../ui/GlassButton';
import { RotateCcw, Truck, ExternalLink } from 'lucide-react';
import { apiClient as api } from '../../services/api';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


interface OrderItem {
  product_name: string;
  price_at_purchase: number;
}

interface Order {
  order_id: string;
  buyer_email: string;
  buyer_name?: string;
  total: number;
  currency: string;
  status: 'pending' | 'succeeded' | 'failed' | 'refunded';
  gateway: string;
  items: OrderItem[];
  created_at: string;
  has_physical: boolean;
  fulfillment_status?: string;
  tracking_number?: string;
  tracking_carrier?: string;
  tracking_url?: string;
  shipped_at?: string;
  shipping_name?: string;
  shipping_line1?: string;
  shipping_city?: string;
  shipping_country?: string;
}

const currencySymbols: Record<string, string> = { USD: '$', GBP: '£', EUR: '€', AUD: 'A$' };

const ProductOrders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Refund modal state
  const [refundOrder, setRefundOrder] = useState<Order | null>(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundError, setRefundError] = useState('');
  const [refundSuccess, setRefundSuccess] = useState('');

  // Fulfill modal state
  const [fulfillOrder, setFulfillOrder] = useState<Order | null>(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingCarrier, setTrackingCarrier] = useState('');
  const [trackingUrl, setTrackingUrl] = useState('');
  const [fulfillNotes, setFulfillNotes] = useState('');
  const [fulfillLoading, setFulfillLoading] = useState(false);
  const [fulfillError, setFulfillError] = useState('');
  const [fulfillSuccess, setFulfillSuccess] = useState('');

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/products/orders');
      setOrders(res.data);
    } catch (err: any) {
      setError('Failed to load orders.');
    } finally {
      setLoading(false);
    }
  };

  const openRefundModal = (order: Order) => {
    setRefundOrder(order);
    setRefundAmount(order.total.toFixed(2));
    setRefundError('');
    setRefundSuccess('');
  };

  const handleRefund = async () => {
    if (!refundOrder) return;
    setRefundLoading(true);
    setRefundError('');
    setRefundSuccess('');
    try {
      await api.post(`/products/orders/${refundOrder.order_id}/refund`, {
        amount: parseFloat(refundAmount),
      });
      setRefundSuccess('Refund issued successfully.');
      setOrders(prev =>
        prev.map(o =>
          o.order_id === refundOrder.order_id ? { ...o, status: 'refunded' } : o
        )
      );
      setTimeout(() => {
        setRefundOrder(null);
        setRefundSuccess('');
      }, 1500);
    } catch (err: any) {
      setRefundError(getApiError(err, 'Refund failed. Please try again.'));
    } finally {
      setRefundLoading(false);
    }
  };

  const openFulfillModal = (order: Order) => {
    setFulfillOrder(order);
    setTrackingNumber('');
    setTrackingCarrier('');
    setTrackingUrl('');
    setFulfillNotes('');
    setFulfillError('');
    setFulfillSuccess('');
  };

  const handleFulfill = async () => {
    if (!fulfillOrder) return;
    setFulfillLoading(true);
    setFulfillError('');
    setFulfillSuccess('');
    try {
      await api.post(`/products/orders/${fulfillOrder.order_id}/fulfill`, {
        tracking_number: trackingNumber || null,
        tracking_carrier: trackingCarrier || null,
        tracking_url: trackingUrl || null,
        notes: fulfillNotes || null,
      });
      setFulfillSuccess('Order marked as shipped. Buyer has been notified.');
      setOrders(prev =>
        prev.map(o =>
          o.order_id === fulfillOrder.order_id
            ? {
                ...o,
                fulfillment_status: 'shipped',
                tracking_number: trackingNumber || undefined,
                tracking_carrier: trackingCarrier || undefined,
                tracking_url: trackingUrl || undefined,
              }
            : o
        )
      );
      setTimeout(() => {
        setFulfillOrder(null);
        setFulfillSuccess('');
      }, 1800);
    } catch (err: any) {
      setFulfillError(getApiError(err, 'Failed to mark as shipped.'));
    } finally {
      setFulfillLoading(false);
    }
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  const formatAmount = (amount: number, currency: string) => {
    const sym = currencySymbols[currency] || currency + ' ';
    return `${sym}${amount.toFixed(2)}`;
  };

  const getFulfillmentBadge = (order: Order) => {
    if (!order.has_physical) return null;
    const status = order.fulfillment_status || 'unfulfilled';
    const labels: Record<string, string> = {
      unfulfilled: 'Unfulfilled',
      shipped: 'Shipped',
      delivered: 'Delivered',
    };
    return (
      <span className={`order-status-badge fulfillment-${status}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) return <div className="products-loading">Loading orders...</div>;

  return (
    <div className="orders-container">
      <div className="orders-header">
        <h2>Orders</h2>
        <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
          {orders.length} total
        </span>
      </div>

      {error && <div className="products-error">{error}</div>}

      {orders.length === 0 ? (
        <div className="products-empty-state">
          <p>No orders yet. Orders will appear here once customers purchase your products.</p>
        </div>
      ) : (
        <div className="orders-table-wrapper">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Buyer</th>
                <th>Items</th>
                <th>Total</th>
                <th>Gateway</th>
                <th>Status</th>
                <th>Fulfillment</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(order => (
                <tr key={order.order_id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatDate(order.created_at)}</td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{order.buyer_name || '—'}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                      {order.buyer_email}
                    </div>
                    {order.has_physical && order.shipping_name && (
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                        {order.shipping_name}, {order.shipping_city}, {order.shipping_country}
                      </div>
                    )}
                  </td>
                  <td>
                    {order.items?.map((item, i) => (
                      <div key={i} style={{ fontSize: '13px' }}>
                        {item.product_name}
                        <span style={{ color: 'var(--text-muted)', marginLeft: '6px' }}>
                          {formatAmount(item.price_at_purchase, order.currency)}
                        </span>
                      </div>
                    ))}
                  </td>
                  <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {formatAmount(order.total, order.currency)}
                  </td>
                  <td style={{ textTransform: 'capitalize', fontSize: '13px' }}>
                    {order.gateway}
                  </td>
                  <td>
                    <span className={`order-status-badge ${order.status}`}>
                      {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                    </span>
                  </td>
                  <td>
                    {getFulfillmentBadge(order)}
                    {order.has_physical && order.tracking_number && (
                      <div style={{ fontSize: '11px', marginTop: '4px', color: 'var(--text-secondary)' }}>
                        {order.tracking_url ? (
                          <a
                            href={order.tracking_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '3px' }}
                          >
                            {order.tracking_number} <ExternalLink size={10} />
                          </a>
                        ) : (
                          order.tracking_number
                        )}
                        {order.tracking_carrier && (
                          <span style={{ marginLeft: '4px' }}>via {order.tracking_carrier}</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {order.status === 'succeeded' && (
                        <GlassButton
                          variant="secondary"
                          size="sm"
                          icon={<RotateCcw size={13} />}
                          onClick={() => openRefundModal(order)}
                        >
                          Refund
                        </GlassButton>
                      )}
                      {order.has_physical && order.fulfillment_status !== 'shipped' && order.status === 'succeeded' && (
                        <GlassButton
                          variant="primary"
                          size="sm"
                          icon={<Truck size={13} />}
                          onClick={() => openFulfillModal(order)}
                        >
                          Mark Shipped
                        </GlassButton>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Refund Modal */}
      {refundOrder && (
        <div className="modal-overlay" onClick={() => !refundLoading && setRefundOrder(null)}>
          <div className="modal-panel" onClick={e => e.stopPropagation()}>
            <h3>Issue Refund</h3>
            <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
              Order from <strong>{refundOrder.buyer_email}</strong> &mdash;{' '}
              total {formatAmount(refundOrder.total, refundOrder.currency)}
            </p>
            <div className="modal-form-group">
              <label>Refund Amount ({refundOrder.currency})</label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                max={refundOrder.total}
                value={refundAmount}
                onChange={e => setRefundAmount(e.target.value)}
              />
            </div>
            {refundError && <div className="modal-error">{refundError}</div>}
            {refundSuccess && (
              <div style={{ marginTop: '12px', fontSize: '14px', color: 'var(--success)' }}>
                {refundSuccess}
              </div>
            )}
            <div className="modal-actions">
              <GlassButton variant="secondary" onClick={() => setRefundOrder(null)} disabled={refundLoading}>
                Cancel
              </GlassButton>
              <GlassButton variant="primary" onClick={handleRefund} loading={refundLoading} icon={<RotateCcw size={14} />}>
                Issue Refund
              </GlassButton>
            </div>
          </div>
        </div>
      )}

      {/* Mark as Shipped Modal */}
      {fulfillOrder && (
        <div className="modal-overlay" onClick={() => !fulfillLoading && setFulfillOrder(null)}>
          <div className="modal-panel" onClick={e => e.stopPropagation()}>
            <h3>Mark as Shipped</h3>
            <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
              Order <strong>#{fulfillOrder.order_id.slice(0, 8).toUpperCase()}</strong> for{' '}
              <strong>{fulfillOrder.buyer_email}</strong>
            </p>
            {fulfillOrder.shipping_name && (
              <div style={{ background: 'var(--panel-strong)', borderRadius: 'var(--radius-md)', padding: '10px 14px', marginBottom: '16px', fontSize: '13px' }}>
                <strong>Ship to:</strong> {fulfillOrder.shipping_name}, {fulfillOrder.shipping_line1}, {fulfillOrder.shipping_city}, {fulfillOrder.shipping_country}
              </div>
            )}
            <div className="modal-form-group">
              <label>Tracking Number (optional)</label>
              <input
                type="text"
                placeholder="e.g. 1Z9999W99999999999"
                value={trackingNumber}
                onChange={e => setTrackingNumber(e.target.value)}
              />
            </div>
            <div className="modal-form-group">
              <label>Carrier (optional)</label>
              <input
                type="text"
                placeholder="e.g. UPS, FedEx, Royal Mail"
                value={trackingCarrier}
                onChange={e => setTrackingCarrier(e.target.value)}
              />
            </div>
            <div className="modal-form-group">
              <label>Tracking URL (optional)</label>
              <input
                type="url"
                placeholder="https://track.example.com/..."
                value={trackingUrl}
                onChange={e => setTrackingUrl(e.target.value)}
              />
            </div>
            <div className="modal-form-group">
              <label>Message to buyer (optional)</label>
              <textarea
                rows={2}
                placeholder="Any additional shipping notes..."
                value={fulfillNotes}
                onChange={e => setFulfillNotes(e.target.value)}
                style={{ resize: 'vertical' }}
              />
            </div>
            {fulfillError && <div className="modal-error">{fulfillError}</div>}
            {fulfillSuccess && (
              <div style={{ marginTop: '12px', fontSize: '14px', color: 'var(--success)' }}>
                {fulfillSuccess}
              </div>
            )}
            <div className="modal-actions">
              <GlassButton variant="secondary" onClick={() => setFulfillOrder(null)} disabled={fulfillLoading}>
                Cancel
              </GlassButton>
              <GlassButton variant="primary" onClick={handleFulfill} loading={fulfillLoading} icon={<Truck size={14} />}>
                Mark as Shipped
              </GlassButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductOrders;
